import { BRAND } from './brand';
import { LIMITS } from './constants';
import type { JobStatus, RegistrationState } from './models';
import { formatReportJobDate } from './report/jobReport';
import { isValidDateString, isValidTimeString, israelDate, israelDateTimeToUtc, shiftDate } from './time';

/**
 * כללי העבודות — משותפים לטופס (web) ולשרת (worker).
 */

export interface JobInput {
  name: string;
  date: string;
  reportTime: string;
  location: string;
  notes?: string;
  /** ISO string או null — מועד סגירת הרשמה אוטומטי */
  registrationClosesAt?: string | null;
}

export interface ValidJobInput {
  name: string;
  date: string;
  reportTime: string;
  location: string;
  notes: string;
  registrationClosesAt: Date | null;
}

export type JobField = 'name' | 'date' | 'reportTime' | 'location' | 'notes' | 'registrationClosesAt';

export type JobValidation = { ok: true; value: ValidJobInput } | { ok: false; errors: Partial<Record<JobField, string>> };

const clean = (v: unknown) => (typeof v === 'string' ? v.trim().replace(/[ \t]+/g, ' ') : '');

export function validateJobInput(input: JobInput, now: Date, opts: { isNew: boolean }): JobValidation {
  const errors: Partial<Record<JobField, string>> = {};
  const name = clean(input.name);
  const location = clean(input.location);
  const notes = typeof input.notes === 'string' ? input.notes.trim() : '';
  const date = clean(input.date);
  const reportTime = clean(input.reportTime);

  if (name.length < 2) errors.name = 'יש להזין שם לעבודה';
  else if (name.length > LIMITS.maxJobNameLength) errors.name = `עד ${LIMITS.maxJobNameLength} תווים`;

  if (!isValidDateString(date)) errors.date = 'יש לבחור תאריך';
  else if (opts.isNew && date < shiftDate(israelDate(now), -1)) errors.date = 'אי אפשר ליצור עבודה בתאריך שעבר';

  if (!isValidTimeString(reportTime)) errors.reportTime = 'יש לבחור שעת התייצבות';

  if (location.length < 2) errors.location = 'יש להזין מיקום';
  else if (location.length > LIMITS.maxLocationLength) errors.location = `עד ${LIMITS.maxLocationLength} תווים`;

  if (notes.length > LIMITS.maxNotesLength) errors.notes = `עד ${LIMITS.maxNotesLength} תווים`;

  let closesAt: Date | null = null;
  if (input.registrationClosesAt) {
    const d = new Date(input.registrationClosesAt);
    if (Number.isNaN(d.getTime())) errors.registrationClosesAt = 'מועד לא תקין';
    else if (d.getTime() <= now.getTime()) errors.registrationClosesAt = 'מועד הסגירה כבר עבר';
    else if (!errors.date && !errors.reportTime && d.getTime() > israelDateTimeToUtc(shiftDate(date, 1), reportTime).getTime()) {
      errors.registrationClosesAt = 'מועד הסגירה מאוחר מדי ביחס לעבודה';
    } else closesAt = d;
  }

  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: { name, date, reportTime, location, notes, registrationClosesAt: closesAt } };
}

/** ארכוב אוטומטי: יום אחרי תאריך העבודה (שעון ישראל), אלא אם יש הגנה מהחזרה ידנית. */
export const AUTO_ARCHIVE_DAYS_AFTER = 1;

export interface ArchiveCheck {
  status: JobStatus;
  date: string;
  archiveHoldUntil: Date | null;
}

export function shouldAutoArchive(job: ArchiveCheck, now: Date, daysAfter: number = AUTO_ARCHIVE_DAYS_AFTER): boolean {
  if (job.status !== 'active') return false;
  if (job.archiveHoldUntil && job.archiveHoldUntil.getTime() > now.getTime()) return false;
  return israelDate(now) >= shiftDate(job.date, daysAfter);
}

/** תאריך העבודה שממנו ומטה עבודות פעילות מועמדות לארכוב (לשאילתה בשרת). */
export function autoArchiveCutoffDate(now: Date, daysAfter: number = AUTO_ARCHIVE_DAYS_AFTER): string {
  return shiftDate(israelDate(now), -daysAfter);
}

/**
 * החזרה ידנית מהארכיון: הגנה מארכוב חוזר עד סוף היום שאחרי ההחזרה.
 * כך יש לפחות יום מלא לעבוד על העבודה לפני שהמערכת תעביר אותה שוב.
 */
export function archiveHoldAfterRestore(now: Date): Date {
  return israelDateTimeToUtc(shiftDate(israelDate(now), 2), '00:00');
}

export interface CloseCheck {
  status: JobStatus;
  registration: RegistrationState;
  registrationClosesAt: Date | null;
}

export function shouldAutoClose(job: CloseCheck, now: Date): boolean {
  return (
    job.status === 'active' &&
    job.registration === 'open' &&
    job.registrationClosesAt !== null &&
    job.registrationClosesAt.getTime() <= now.getTime()
  );
}

/** הקישור הציבורי להרשמה. */
export function registrationUrl(origin: string, slug: string): string {
  return `${origin.replace(/\/+$/, '')}/j/${encodeURIComponent(slug)}`;
}

/** הודעת הזמנה ממותגת לשליחה לקבלנים ולעובדים (שיתוף ידני בוואטסאפ). */
export function buildInvitationMessage(
  job: { name: string; date: string; reportTime: string; location: string },
  link: string,
  accessCode: string,
  signature: string = BRAND.whatsapp.signature,
): string {
  return [
    `*${BRAND.name} — הרשמה לעבודה*`,
    `*${job.name}*`,
    '',
    `📅 ${formatReportJobDate(job.date)}`,
    `🕐 התייצבות: ${job.reportTime}`,
    `📍 ${job.location}`,
    '',
    'להרשמה (קבלנים ועובדים):',
    link,
    `קוד כניסה: *${accessCode}*`,
    '',
    signature,
  ].join('\n');
}

/** השדות שעריכתם מצדיקה הודעת עדכון בוואטסאפ. */
export const NOTIFY_FIELDS = ['date', 'reportTime', 'location'] as const;
